import React, { lazy, Suspense } from 'react';

import { Image, Space, Typography } from 'antd';
import { useTranslation } from 'react-i18next';

import { Icon } from 'components/base/Icon';
import { Play } from 'components/base/SVG';
import { AttachmentType, SocialPostAttachment } from 'types/AmplifiedTemplate';

// Pulls in react-pdf/pdfjs-dist and PDF worker init — only Pdf-type
// attachments need that cost, so the far more common image/video previews
// shouldn't pay for it on every MediaView render (Copilot review, PR #6799).
const PostPdfPreview = lazy(
  () =>
    import(
      'routes/Advocate/PostModal/RightPreview/PostPreviewContent/PostPdfPreview'
    ),
);

type MediaViewProps = {
  item: SocialPostAttachment;
  isPostPreview?: boolean;
  mediaClassName?: string;
};

interface PdfMediaViewProps {
  itemId: string;
  url?: string;
  thumbnailUrl?: string;
  isPostPreview?: boolean;
}

// Split out of MediaView to keep its own branching (post-preview carousel
// vs. card/list thumbnail, with or without a thumbnail_url) from adding to
// MediaView's cognitive complexity.
const PdfMediaView = ({
  itemId,
  url,
  thumbnailUrl,
  isPostPreview,
}: PdfMediaViewProps) => {
  const { t } = useTranslation();

  if (!isPostPreview) {
    if (!thumbnailUrl) {
      return (
        <div className="flex w-full h-full items-center justify-center bg-gray-100">
          <Icon
            name="pdf"
            size={80}
            ariaLabel={`Post template attachment ${itemId}`}
          />
        </div>
      );
    }
    return (
      <Image
        preview={false}
        src={thumbnailUrl}
        rootClassName="w-full !h-full"
        alt={`Post template attachment ${itemId}`}
        className="w-full !h-full object-cover"
      />
    );
  }

  if (!url) return null;

  return (
    <Suspense
      fallback={
        <div className="py-10 text-center text-sm text-[#6A6991]">
          {t('LoadingPDFPreview')}
        </div>
      }
    >
      <PostPdfPreview uploadedPdf={url} />
    </Suspense>
  );
};

export default function MediaView({ item, isPostPreview }: MediaViewProps) {
  const { url, type, thumbnail_url } = item.attachment;

  if (type === AttachmentType.Video) {
    return (
      <div className="relative w-full !h-full">
        {thumbnail_url ? (
          <Image
            preview={false}
            src={thumbnail_url}
            rootClassName="w-full !h-full"
            alt="video thumbnail"
            className="w-full !h-full object-cover"
          />
        ) : (
          <video src={url} className="w-full h-full object-cover" />
        )}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="rounded-full bg-black/45 w-12 h-12 flex items-center justify-center">
            <Play />
          </div>
        </div>
      </div>
    );
  }

  if (type === AttachmentType.Pdf) {
    return (
      <PdfMediaView
        itemId={item.id}
        url={url}
        thumbnailUrl={thumbnail_url}
        isPostPreview={isPostPreview}
      />
    );
  }

  if (type === AttachmentType.Image) {
    return (
      <Image
        preview={false}
        src={url}
        rootClassName="w-full !h-full"
        alt={`Post template attachment ${item.id}`}
        className="w-full !h-full object-cover"
      />
    );
  }

  if (type === AttachmentType.Url) {
    if (!thumbnail_url) {
      return null;
    }

    if (isPostPreview) {
      return (
        <div className="flex gap-3 border border-str200 rounded-xl p-3.5 my-3">
          <div className="relative w-48 h-28 shrink-0 rounded-lg">
            <Image
              rootClassName="w-full h-full"
              preview={false}
              className="object-cover !h-full rounded-lg border-none"
              alt={
                item.attachment?.title?.slice(0, 100) || 'Link preview image'
              }
              src={thumbnail_url}
            />
          </div>
          <Space direction="vertical" size={4}>
            <Typography.Title
              level={5}
              className="!mb-0 line-clamp-2 cursor-default"
            >
              {item.attachment?.title}
            </Typography.Title>
            <Typography.Link
              href={item.attachment?.url}
              target="_blank"
              rel="noopener noreferrer"
              className="!text-xs line-clamp-4"
            >
              {item.attachment?.url}
            </Typography.Link>
          </Space>
        </div>
      );
    }

    return (
      <Image
        preview={false}
        rootClassName="w-full !h-full"
        src={thumbnail_url}
        alt={`Post template attachment ${item.id}`}
        className="w-full !h-full object-cover"
      />
    );
  }

  return null;
}
